/**
 * Sub-controller for Test Drive Flyout — Booking Confirmation (terminal view).
 *
 * Sets `state` to `single` or `multiple` from the Preferred Model count, then fills
 * authored placeholders from Choose Models and Confirm Honda Centre markup. Does not
 * read the submit response or issue a second request.
 */
export const testDriveConfirmation = {
  /**
   * Bootstrap the Booking Confirmation view. Idempotent; safe to call once at boot.
   */
  testDriveConfirmationView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * @private
     * @readonly
     */
    STATE_SINGLE: 'single',

    /**
     * @private
     * @readonly
     */
    STATE_MULTIPLE: 'multiple',

    /**
     * @private
     * @readonly
     */
    CENTRE_NAME_SELECTOR: '.test-drive-confirmation__centre-name',

    /**
     * @private
     * @readonly
     */
    MODELS_SELECTOR: '.test-drive-confirmation__models',

    /**
     * @private
     *
     * @type {boolean}
     */
    isWired: false,

    /**
     * @private
     *
     * @type {{
     *   view: HTMLElement | null
     * }}
     */
    elements: {
      view: null
    },

    /**
     * Lazy-wire on first show; refresh state and placeholders on every enter.
     *
     * Listens to the pre-swap event, not `amb:modal-view-changed`, so the copy is in place
     * before the view slides into sight.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-change', event => {
        if (event.detail?.view !== 'test-drive-confirmation') {
          return;
        }

        this.wire();
        this.refresh();
      });
    },

    /**
     * Resolve the confirmation view ref once.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector(
        '.configurator-modal .modal-view[view="test-drive-confirmation"]'
      );

      if (!view) {
        return false;
      }

      this.elements.view = view;
      this.isWired = true;

      return true;
    },

    /**
     * Apply variant state and fill placeholders from sibling step markup.
     *
     * @private
     */
    refresh() {
      const view = this.elements.view;

      if (!view) {
        return;
      }

      const modelNames = this.getChosenModelNames();
      const modelCount = this.getChosenModelCount();
      const state = modelCount > 1 ? this.STATE_MULTIPLE : this.STATE_SINGLE;

      view.setAttribute('state', state);
      this.fillCentreName(this.getConfirmedCentreName());
      this.fillModels(modelNames);
    },

    /**
     * Number of checked Preferred Model inputs in step 1 markup.
     *
     * @returns {number}
     *
     * @private
     */
    getChosenModelCount() {
      return document.querySelectorAll(
        '.configurator-modal .modal-view[view="test-drive"] .test-drive-model-list .model--input:checked'
      ).length;
    },

    /**
     * Checked Preferred Model display names from step 1 markup, in DOM order.
     *
     * @returns {string[]}
     *
     * @private
     */
    getChosenModelNames() {
      const inputs = document.querySelectorAll(
        '.configurator-modal .modal-view[view="test-drive"] .test-drive-model-list .model--input:checked'
      );

      return Array.from(inputs).map(input => {
        const nameEl = input.closest('.model-card')?.querySelector('.model--name');
        const label = (nameEl?.textContent || '').trim();

        return label || input.dataset.productId || input.value || '';
      }).filter(Boolean);
    },

    /**
     * Confirmed Honda Centre display name from step 2 markup.
     *
     * @returns {string}
     *
     * @private
     */
    getConfirmedCentreName() {
      const selected = document.querySelector(
        '.configurator-modal .modal-view[view="test-drive-honda-centre"] ' +
        'input[name="test-drive-location-dealers"]:checked'
      );

      return (selected?.dataset.dealername || '').trim();
    },

    /**
     * Write the centre name into every placeholder; no-op when none exist.
     *
     * An empty name is left alone rather than written through: blanking the span mid-sentence
     * reads worse than whatever the author put there.
     *
     * @param {string} centreName
     *
     * @private
     */
    fillCentreName(centreName) {
      const placeholders = this.elements.view?.querySelectorAll(this.CENTRE_NAME_SELECTOR);

      if (!centreName || !placeholders?.length) {
        return;
      }

      placeholders.forEach(el => {
        el.textContent = centreName;
      });
    },

    /**
     * Replace model-list children; no-op when the list element is absent or there is
     * nothing to write, so a mis-authored asset keeps its own content.
     *
     * @param {string[]} modelNames
     *
     * @private
     */
    fillModels(modelNames) {
      const lists = this.elements.view?.querySelectorAll(this.MODELS_SELECTOR);

      if (!modelNames.length || !lists?.length) {
        return;
      }

      lists.forEach(list => {
        list.innerHTML = '';

        modelNames.forEach(name => {
          const item = document.createElement('li');

          item.textContent = name;
          list.appendChild(item);
        });
      });
    }
  }
};
